/*
 * A tiny external store so the case header (rendered above the IDE, outside
 * its providers) can collapse and expand the IDE's side panels. `CaseIde`
 * registers the imperative panel handles while mounted; the header reads the
 * collapsed flags and calls toggle(), and whatever hands Casey work calls
 * show("right").
 *
 * It also owns the focus modes: one pane (Case Files, Work Products, the open
 * document or Casey) made big, the rest of the layout remembered and put back
 * by restore(). A focus is for the moment, so it is not persisted, and while
 * one is on `CaseIde` keeps the stored layout at the one it replaced.
 */
import { useCallback, useSyncExternalStore } from "react";
import type {
  GroupImperativeHandle,
  Layout,
  PanelImperativeHandle
} from "react-resizable-panels";

export type IdePanelId = "left" | "right";
/** The pane a focus mode makes big. */
export type IdeFocus = "files" | "workspace" | "document" | "casey";
export type IdeSectionId = "files" | "workspace";
export type IdeGroupId = "root" | "explorer";

/** Panel ids of the IDE's two resizable groups (also their storage keys). */
export const IDE_PANELS = {
  left: "ide-left",
  center: "ide-center",
  right: "ide-right",
  files: "ide-files",
  workspace: "ide-workspace"
} as const;

/** Below this viewport width a focused side pane takes 70% instead of 60%. */
export const NARROW_VIEWPORT_PX = 1280;

/** The root layout for a focus mode at a given viewport width. */
export const focusLayout = (focus: IdeFocus, viewportWidth: number): Layout => {
  const side = viewportWidth < NARROW_VIEWPORT_PX ? 70 : 60;
  const left = focus === "files" || focus === "workspace" ? side : 0;
  const right = focus === "casey" ? side : 0;
  return {
    [IDE_PANELS.left]: left,
    [IDE_PANELS.center]: 100 - left - right,
    [IDE_PANELS.right]: right
  };
};

type State = {
  /** Whether an IDE is mounted at all (controls whether toggles render). */
  mounted: boolean;
  collapsed: Record<IdePanelId, boolean>;
  /**
   * Collapsed by `makeRoom` rather than by the user, so the header can say
   * why the panel went away. Cleared as soon as the panel is shown again.
   */
  autoHidden: Record<IdePanelId, boolean>;
  /** The pane currently made big, if any. */
  focus: IdeFocus | null;
};

const NOT_HIDDEN: Record<IdePanelId, boolean> = { left: false, right: false };

const handles: Partial<Record<IdePanelId, PanelImperativeHandle>> = {};
const sections: Partial<Record<IdeSectionId, PanelImperativeHandle>> = {};
const groups: Partial<Record<IdeGroupId, GroupImperativeHandle>> = {};
/** The layouts a focus replaced, put back by restore(). */
let beforeFocus: Partial<Record<IdeGroupId, Layout>> | null = null;
let state: State = {
  mounted: false,
  collapsed: { left: false, right: false },
  autoHidden: NOT_HIDDEN,
  focus: null
};
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());
const setState = (next: State) => {
  state = next;
  emit();
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

/**
 * Read a handle's collapsed flag, tolerating a panel whose group is mid-teardown
 * (react-resizable-panels throws "Group ... not found" from isCollapsed() while
 * the group unmounts).
 */
const readCollapsed = (handle?: PanelImperativeHandle): boolean => {
  try {
    return handle?.isCollapsed() ?? false;
  } catch {
    return false;
  }
};

/** Same tolerance for a group mid-teardown; `{}` before its first layout. */
const readLayout = (group?: GroupImperativeHandle): Layout | undefined => {
  try {
    const layout = group?.getLayout();
    return layout && Object.keys(layout).length ? { ...layout } : undefined;
  } catch {
    return undefined;
  }
};

const applyLayout = (group: GroupImperativeHandle | undefined, l?: Layout) => {
  if (!group || !l) return;
  try {
    group.setLayout(l);
  } catch {
    // Group unmounting: nothing left to lay out.
  }
};

/** A panel that is showing is no longer auto-hidden. */
const settleAutoHidden = (
  collapsed: Record<IdePanelId, boolean>,
  autoHidden: Record<IdePanelId, boolean>
): Record<IdePanelId, boolean> => {
  const next = {
    left: autoHidden.left && collapsed.left,
    right: autoHidden.right && collapsed.right
  };
  return next.left === autoHidden.left && next.right === autoHidden.right
    ? autoHidden
    : next;
};

export const ideLayoutStore = {
  register(id: IdePanelId, handle: PanelImperativeHandle | null) {
    if (handle) handles[id] = handle;
    else delete handles[id];
    const mounted = Boolean(handles.left || handles.right);
    const collapsed = {
      left: readCollapsed(handles.left),
      right: readCollapsed(handles.right)
    };
    const next: State = {
      mounted,
      collapsed,
      autoHidden: mounted
        ? settleAutoHidden(collapsed, state.autoHidden)
        : NOT_HIDDEN,
      focus: mounted ? state.focus : null
    };
    if (!mounted) beforeFocus = null;
    if (
      next.mounted !== state.mounted ||
      next.collapsed.left !== state.collapsed.left ||
      next.collapsed.right !== state.collapsed.right ||
      next.autoHidden !== state.autoHidden ||
      next.focus !== state.focus
    ) {
      setState(next);
    }
  },
  /** The Case Files / Work Products panels of the explorer's split. */
  registerSection(id: IdeSectionId, handle: PanelImperativeHandle | null) {
    if (handle) sections[id] = handle;
    else delete sections[id];
  },
  /** The root (left, center, right) and explorer (files, workspace) groups. */
  registerGroup(id: IdeGroupId, handle: GroupImperativeHandle | null) {
    if (handle) {
      groups[id] = handle;
      return;
    }
    delete groups[id];
    if (id === "root") {
      beforeFocus = null;
      if (state.focus) setState({ ...state, focus: null });
    }
  },
  /** Whether a focus mode is on (its layout is not the one to persist). */
  isFocused(): boolean {
    return state.focus !== null;
  },
  /**
   * Make one pane big: the explorer wide with the chosen section at full
   * height, the document alone, or Casey wide. Switching between focus modes
   * keeps the layout from before the first one.
   */
  focus(target: IdeFocus) {
    const { root } = groups;
    if (!root) return;
    if (!state.focus) {
      beforeFocus = {
        root: readLayout(root),
        explorer: readLayout(groups.explorer)
      };
    }
    setState({ ...state, focus: target });
    applyLayout(root, focusLayout(target, window.innerWidth));
    if (target === "files" || target === "workspace") {
      const mine = sections[target];
      const other = sections[target === "files" ? "workspace" : "files"];
      if (readCollapsed(mine)) mine?.expand();
      if (!readCollapsed(other)) other?.collapse();
    }
    ideLayoutStore.sync();
  },
  /** Put back the layout the focus replaced. */
  restore() {
    if (!state.focus) return;
    const before = beforeFocus;
    beforeFocus = null;
    setState({ ...state, focus: null });
    applyLayout(groups.root, before?.root);
    applyLayout(groups.explorer, before?.explorer);
    ideLayoutStore.sync();
  },
  /** The pane's maximize control and shortcut: focus it, or restore. */
  toggleFocus(target: IdeFocus) {
    if (state.focus === target) ideLayoutStore.restore();
    else ideLayoutStore.focus(target);
  },
  /** Re-read collapsed flags from the handles (call from Panel onResize). */
  sync() {
    const next: Record<IdePanelId, boolean> = {
      left: readCollapsed(handles.left),
      right: readCollapsed(handles.right)
    };
    const autoHidden = settleAutoHidden(next, state.autoHidden);
    if (
      next.left !== state.collapsed.left ||
      next.right !== state.collapsed.right ||
      autoHidden !== state.autoHidden
    ) {
      setState({ ...state, collapsed: next, autoHidden });
    }
  },
  /**
   * Show or hide a panel as it looks now. During a focus the layout from
   * before comes back first, then the panel ends up the other way round from
   * how the focus had it.
   */
  toggle(id: IdePanelId) {
    const h = handles[id];
    if (!h) return;
    const wasCollapsed = readCollapsed(h);
    ideLayoutStore.restore();
    if (wasCollapsed) {
      if (readCollapsed(h)) h.expand();
    } else if (!readCollapsed(h)) {
      h.collapse();
    }
    ideLayoutStore.sync();
  },
  /**
   * Expand a panel if it is collapsed (something wants it seen). A focus that
   * hid it gives way to the layout from before.
   */
  show(id: IdePanelId) {
    const h = handles[id];
    if (!h || !readCollapsed(h)) return;
    ideLayoutStore.restore();
    if (readCollapsed(h)) h.expand();
    ideLayoutStore.sync();
  },
  /**
   * A document is opening: maximized Casey hides the centre pane, so it gives
   * way to the layout from before. Other focus modes keep the centre showing.
   */
  showDocument() {
    if (state.focus === "casey") ideLayoutStore.restore();
  },
  /**
   * Collapse a panel to give the centre pane more width, remembering that it
   * was not the user's doing. A panel the user already collapsed is left as
   * it is and not flagged, and so is one the user made big.
   */
  makeRoom(id: IdePanelId) {
    const h = handles[id];
    if (!h || readCollapsed(h)) return;
    if (
      id === "left" &&
      (state.focus === "files" || state.focus === "workspace")
    )
      return;
    if (id === "right" && state.focus === "casey") return;
    h.collapse();
    ideLayoutStore.sync();
    if (!state.collapsed[id]) return;
    setState({ ...state, autoHidden: { ...state.autoHidden, [id]: true } });
  },
  /** Undo `makeRoom`, unless the user has shown the panel since. */
  releaseRoom(id: IdePanelId) {
    const h = handles[id];
    if (!h || !state.autoHidden[id]) return;
    if (readCollapsed(h)) h.expand();
    ideLayoutStore.sync();
  },
  /** Test-only reset. */
  resetForTests() {
    delete handles.left;
    delete handles.right;
    delete sections.files;
    delete sections.workspace;
    delete groups.root;
    delete groups.explorer;
    beforeFocus = null;
    state = {
      mounted: false,
      collapsed: { left: false, right: false },
      autoHidden: NOT_HIDDEN,
      focus: null
    };
    emit();
  }
};

export const useIdeLayout = (): State =>
  useSyncExternalStore(
    subscribe,
    () => state,
    () => state
  );

/* The USCIS pane's open state, remembered per case in localStorage. */

const uscisKey = (caseKey: string) => `caseIde.uscisOpen.${caseKey}`;
const uscisListeners = new Set<() => void>();

const subscribeUscis = (l: () => void) => {
  uscisListeners.add(l);
  return () => {
    uscisListeners.delete(l);
  };
};

export const readUscisPaneOpen = (caseKey: string): boolean => {
  try {
    return localStorage.getItem(uscisKey(caseKey)) === "1";
  } catch {
    return false;
  }
};

export const setUscisPaneOpen = (caseKey: string, open: boolean) => {
  try {
    if (open) localStorage.setItem(uscisKey(caseKey), "1");
    else localStorage.removeItem(uscisKey(caseKey));
  } catch {
    // Storage unavailable: the pane still toggles for this session.
  }
  uscisListeners.forEach((l) => l());
};

export const useUscisPaneOpen = (
  caseKey: string
): [boolean, (open: boolean) => void] => {
  const open = useSyncExternalStore(
    subscribeUscis,
    () => readUscisPaneOpen(caseKey),
    () => false
  );
  const setOpen = useCallback(
    (next: boolean) => setUscisPaneOpen(caseKey, next),
    [caseKey]
  );
  return [open, setOpen];
};
